import { LDProvider, useFlags as useLDFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDContext } from 'launchdarkly-js-client-sdk';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { resolveLdClientId } from '../api/resolveApiBaseUrl';

export type FlagKeys =
  | 'ionic_enabled'
  | 'ionic_forms_enabled'
  | 'ionic_killswitch'
  | 'budget_enabled'
  | 'citizen_enabled'
  | 'citizen-legislation'
  | 'next-gen-reporting';

export type FlagMap = Record<FlagKeys, boolean>;

/** User info for LaunchDarkly targeting (passed after login) */
export interface LDUserInfo {
  id: number | string;
  email?: string;
  displayName?: string;
  username?: string;
  role?: string;
  accountType?: string;
  organizationId?: number | string;
  organizationName?: string;
  subscription?: string;
}

export const defaultFlags: FlagMap = {
  ionic_enabled: false,
  ionic_forms_enabled: false,
  ionic_killswitch: false,
  budget_enabled: false,
  // Citizen app ships dark. Off means /citizen/* renders the coming-soon page.
  citizen_enabled: false,
  /*
    Next Gen Reporting rollout (2026-08-19). OFF means /reports renders the
    legacy reporting surface — the production default until NGR is
    feature-complete. The client value only picks which UI mounts; the backend
    evaluates the same flag authoritatively and 404s every /api/ngr route for
    unflagged orgs. Defaulting false here also means an unreachable
    LaunchDarkly degrades to legacy, never to a half-built surface.
  */
  /*
    Citizen Legislation surfaces (2026-08-19). OFF hides the Legislation nav
    entry, the profile menu's "Bills I'm watching", and the Tracking card on
    Show it All. The routes stay mounted so notification deep-links still
    resolve — this gates discovery, not access.
  */
  'citizen-legislation': false,
  'next-gen-reporting': false,
};

const getEnvBoolean = (value: string | undefined) => {
  if (value === undefined) {
    return undefined;
  }
  return value === '1' || value.toLowerCase() === 'true';
};

const getForcedFlags = (): FlagMap | null => {
  const forceFlags = getEnvBoolean(import.meta.env.VITE_FORCE_IONIC_FLAGS);
  if (!forceFlags) {
    return null;
  }

  const forced: FlagMap = { ...defaultFlags };
  const envOverrides: Partial<FlagMap> = {
    ionic_enabled: getEnvBoolean(import.meta.env.VITE_IONIC_ENABLED),
    ionic_forms_enabled: getEnvBoolean(import.meta.env.VITE_IONIC_FORMS_ENABLED),
    ionic_killswitch: getEnvBoolean(import.meta.env.VITE_IONIC_KILLSWITCH),
    citizen_enabled: getEnvBoolean(import.meta.env.VITE_CITIZEN_ENABLED),
  };

  (Object.keys(envOverrides) as FlagKeys[]).forEach((key) => {
    const value = envOverrides[key];
    if (typeof value === 'boolean') {
      forced[key] = value;
    }
  });

  return forced;
};

/**
 * Build an LDContext from user info.
 *
 * Multi-kind (user + organization) so org-targeted rollout rules — the NGR
 * flag targets organizations (R12) — match the same context shape the backend
 * server SDK sends (launchDarklyService.buildContext). The user kind keeps
 * every attribute it always had, so existing user-targeted rules
 * (ionic_*, citizen_enabled, budget_enabled) are unaffected on web and native.
 */
const buildUserContext = (user: LDUserInfo): LDContext => {
  const userContext = {
    key: String(user.id),
    email: user.email,
    name: user.displayName,
    username: user.username,
    role: user.role || user.accountType || 'user',
    accountType: user.accountType,
    organizationId: user.organizationId ? String(user.organizationId) : undefined,
    organizationName: user.organizationName,
    subscription: user.subscription,
  };
  if (!user.organizationId) {
    return { kind: 'user', ...userContext };
  }
  return {
    kind: 'multi',
    user: userContext,
    organization: {
      key: String(user.organizationId),
      name: user.organizationName,
    },
  };
};

const ANONYMOUS_CONTEXT: LDContext = {
  kind: 'user',
  key: 'anonymous',
  anonymous: true,
};

interface FlagContextValue {
  flags: FlagMap;
  ready: boolean;
  /** Call after login to set user context for targeted flag evaluation */
  identifyUser: (user: LDUserInfo) => Promise<void>;
  /** Call on logout to reset to anonymous context */
  resetUser: () => Promise<void>;
}

const noopAsync = async () => {};

const FlagContext = createContext<FlagContextValue>({
  flags: defaultFlags,
  ready: false,
  identifyUser: noopAsync,
  resetUser: noopAsync,
});

/**
 * Inner component that uses LaunchDarkly hooks and applies kill switch logic
 */
const FlagProcessor: React.FC<React.PropsWithChildren> = ({ children }) => {
  const rawFlags = useLDFlags() as Partial<FlagMap>;
  const client = useLDClient();
  const forcedFlags = useMemo(() => getForcedFlags(), []);

  // Send track event on mount
  useEffect(() => {
    if (client) {
      client.track('sdk-initialized', { source: 'cursor' });
      if (import.meta.env.DEV) {
        console.log('LaunchDarkly SDK connected — sdk-initialized tracked');
      }
    }
  }, [client]);

  /*
    Dev-only flag diagnostic. Every way a flag can be misconfigured looks
    identical from the UI: a flag missing from this environment, a flag not
    marked "SDK available to client-side", a targeting rule that swallows
    every context, or LaunchDarkly being unreachable all land on the same
    defaultFlags fallback that "the feature is off" does. Print what LD
    actually served, next to the environment it came from, so the dashboard
    and the app can be compared without guessing.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || !client) {
      return undefined;
    }

    // An empty payload means the SDK has not finished initializing — every key
    // is "missing" at that point, which is noise rather than a finding.
    if (Object.keys(rawFlags).length === 0) {
      const timer = setTimeout(() => {
        console.warn(
          `[flags] LaunchDarkly (${resolveLdClientId()}) returned no flags after 5s — every flag is falling back to its code default. Check network access to *.launchdarkly.com (ad blockers commonly block it).`,
        );
      }, 5000);
      return () => clearTimeout(timer);
    }

    const keys = Object.keys(defaultFlags) as FlagKeys[];
    console.log(
      `[flags] LaunchDarkly (${resolveLdClientId()}) served:`,
      Object.fromEntries(
        keys.map((key) => [
          key,
          typeof rawFlags[key] === 'boolean' ? rawFlags[key] : `${defaultFlags[key]} — code default, LD sent nothing`,
        ]),
      ),
    );

    const missing = keys.filter((key) => typeof rawFlags[key] !== 'boolean');
    if (missing.length > 0) {
      console.warn(
        `[flags] LaunchDarkly sent no value for: ${missing.join(', ')}. The code default applies. Confirm each flag exists in THIS environment and is marked "SDK available to client-side" — a flag the SDK never returns is indistinguishable from one that is off.`,
      );
    }

    return undefined;
  }, [client, rawFlags]);

  // Re-identify with user context after login (enables percentage rollouts & targeting)
  const identifyUser = useCallback(async (user: LDUserInfo) => {
    if (!client) return;
    const context = buildUserContext(user);
    await client.identify(context);
    if (import.meta.env.DEV) {
      console.log('LaunchDarkly identify:', user.email || user.id);
    }
  }, [client]);

  // Reset to anonymous context on logout
  const resetUser = useCallback(async () => {
    if (!client) return;
    await client.identify(ANONYMOUS_CONTEXT);
    if (import.meta.env.DEV) {
      console.log('LaunchDarkly context reset to anonymous');
    }
  }, [client]);

  const flags = useMemo(() => {
    let processedFlags: FlagMap;

    if (forcedFlags) {
      // Use env-var-forced flags
      processedFlags = { ...forcedFlags };
    } else {
      // Start with defaults, apply flags from LaunchDarkly
      processedFlags = { ...defaultFlags };
      (Object.keys(defaultFlags) as FlagKeys[]).forEach((key) => {
        const value = rawFlags[key];
        if (typeof value === 'boolean') {
          processedFlags[key] = value;
        }
      });
    }

    // KILL SWITCH: If enabled, force all Ionic flags to false (instant rollback to legacy).
    // Applied after BOTH forced flags and LaunchDarkly flags so it always takes precedence.
    if (processedFlags.ionic_killswitch) {
      console.warn('IONIC KILL SWITCH ACTIVE - Reverting to legacy shell');
      processedFlags.ionic_enabled = false;
      processedFlags.ionic_forms_enabled = false;
    }

    return processedFlags;
  }, [rawFlags, forcedFlags]);

  const contextValue: FlagContextValue = {
    flags,
    ready: true, // React SDK handles loading state internally
    identifyUser,
    resetUser,
  };

  return (
    <FlagContext.Provider value={contextValue}>
      {children}
    </FlagContext.Provider>
  );
};

/**
 * Main provider component that wraps the app with LaunchDarkly
 */
export const FeatureFlagProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const clientId = resolveLdClientId();
  const forcedFlags = getForcedFlags();

  // If forcing flags via env vars, bypass LaunchDarkly entirely
  if (forcedFlags) {
    // Apply kill switch even to forced flags — it must always win
    const resolvedFlags = { ...forcedFlags };
    if (resolvedFlags.ionic_killswitch) {
      resolvedFlags.ionic_enabled = false;
      resolvedFlags.ionic_forms_enabled = false;
    }

    return (
      <FlagContext.Provider
        value={{
          flags: resolvedFlags,
          ready: true,
          identifyUser: noopAsync,
          resetUser: noopAsync,
        }}
      >
        {children}
      </FlagContext.Provider>
    );
  }

  // If no client ID, use defaults
  if (!clientId) {
    return (
      <FlagContext.Provider
        value={{
          flags: defaultFlags,
          ready: true,
          identifyUser: noopAsync,
          resetUser: noopAsync,
        }}
      >
        {children}
      </FlagContext.Provider>
    );
  }

  // Use LaunchDarkly React SDK — start anonymous, call identifyUser() after login
  return (
    <LDProvider
      clientSideID={clientId}
      context={ANONYMOUS_CONTEXT}
      options={{
        bootstrap: 'localStorage', // Cache flags in localStorage for faster loads
      }}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
      deferInitialization={false}
    >
      <FlagProcessor>{children}</FlagProcessor>
    </LDProvider>
  );
};

/**
 * Hook to access feature flags with kill switch logic applied
 */
export const useFeatureFlags = (): FlagContextValue => useContext(FlagContext);

/**
 * Re-export official LaunchDarkly hooks for convenience
 * 
 * @example Send custom track events:
 * import { useLDClient } from '@shared/flags/FeatureFlagProvider';
 * const client = useLDClient();
 * client?.track('custom-event', { source: 'cursor', userId: '123' });
 * 
 * @example Access raw flags (without killswitch logic):
 * import { useLDFlagsDirect } from '@shared/flags/FeatureFlagProvider';
 * const rawFlags = useLDFlagsDirect();
 */
export { useLDClient, useFlags as useLDFlagsDirect } from 'launchdarkly-react-client-sdk';
